import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as invoiceService from '../services/invoiceService';
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';
import useStoreInfo from '../../../hooks/useCompanyInfo';
import {
  Box, Button, Card, CardContent, Typography, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Divider, Chip, Container, Alert, AlertTitle,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel,
  Select, MenuItem, FormControlLabel, Switch, CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import PaymentIcon from '@mui/icons-material/Payment';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from "react-i18next";
import { toast } from 'react-toastify';
import { calculateInvoiceTotals, formatDate as formatDateUtil, getPaymentStatus } from '../utils/invoiceCalculations';
import { generateInvoicePDFComplete, renderInvoiceTemplate } from '../utils/invoicePdfUtils';
import { generateAndPreviewPDF } from '../services/invoicePdfService';
import { PAYMENT_STATUS } from '../../../constants/paymentStatus';

const InvoiceViewPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // ✅ Get store info from Redux store (auto-fetches if needed)
  const { storeInfo } = useStoreInfo();
  
  // ✅ Get payment methods from Redux store
  const { paymentMethods: paymentMethodOptions, loading: paymentMethodsLoading } = useSelector(
    (state) => state.paymentMethod
  );
  
  // Payment dialog state
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [isFullPayment, setIsFullPayment] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Peppol dialog state
  const [openPeppolDialog, setOpenPeppolDialog] = useState(false);
  const [peppolSendOnPeppol, setPeppolSendOnPeppol] = useState(false);
  const [peppolAttachPdf, setPeppolAttachPdf] = useState(false);
  const [sendingToPeppol, setSendingToPeppol] = useState(false);
  const [peppolPreviewHtml, setPeppolPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Email dialog state - UI visible but functionality disabled
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [loadingEmailPreview, setLoadingEmailPreview] = useState(false);
  
  // PDF preview loading state
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);

  // Aggregate payments by payment method (distinct) - MUST be before early returns
  const aggregatedPayments = React.useMemo(() => {
    if (!invoice || !invoice.Payments || invoice.Payments.length === 0) return [];
    
    const paymentMap = {};
    invoice.Payments.forEach((payment) => {
      const methodName = payment.PaymentMethodName || payment.PaymentMethod?.Name || 'Unknown Method';
      const amount = Number(payment.Amount || 0);
      
      if (paymentMap[methodName]) {
        paymentMap[methodName] += amount;
      } else {
        paymentMap[methodName] = amount;
      }
    });
    
    return Object.entries(paymentMap).map(([methodName, totalAmount]) => ({
      methodName,
      totalAmount
    }));
  }, [invoice]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch invoice - service already extracts payload
        const invoiceData = await invoiceService.getInvoiceById(id);
        setInvoice(invoiceData);
        
        // ✅ Fetch payment methods from Redux store if not already loaded
        if (!paymentMethodOptions || paymentMethodOptions.length === 0) {
          dispatch(fetchPaymentMethods());
        }
      } catch (error) {
        console.error('Error fetching invoice:', error);
        setInvoice(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, dispatch, paymentMethodOptions]);

  if (loading) return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography>Loading...</Typography>
    </Container>
  );

  if (!invoice) return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Alert severity="error">
        <AlertTitle>Not Found</AlertTitle>
        Invoice not found
      </Alert>
      <Button onClick={() => navigate('/invoices')} sx={{ mt: 2 }}>
        Back to Invoices
      </Button>
    </Container>
  );

  // Extract customer data
  const customer = invoice.Customer || {};
  const customerName = customer.Name || invoice.CustomerName || 'N/A';
  const customerVAT = customer.VATNumber || invoice.CustomerVAT || 'N/A';
  const customerEmail = customer.Email || invoice.CustomerEmail || 'N/A';
  const customerPhone = customer.PhoneNumber || customer.MobilePhone || invoice.CustomerPhone || 'N/A';
  const customerAddress = customer.Street || invoice.CustomerAddress || 'N/A';
  const customerCity = customer.City || invoice.CustomerCity || '';
  const customerPostCode = customer.PostCode || '';
  const customerCountry = customer.Country || '';

  // ✅ Use stored invoice totals to avoid rounding issues from recalculation
  const lineItems = invoice.LineItems || [];
  const vatPercentage = Number(invoice.VATPercentage || 21);
  
  // Use stored invoice values (already calculated correctly when invoice was created)
  const totalIncl = Number(invoice.InvoiceTotalAmount || 0);
  const paidAmount = Number(invoice.InvoicePaidAmount || 0);
  const dueAmount = Number(invoice.InvoiceDueAmount || (totalIncl - paidAmount));
  
  // Use stored subtotal and VAT values from backend
  const totalExcl = Number(invoice.InvoiceSubTotal || 0);
  const totalVAT = Number(invoice.InvoiceTotalVATAmount || 0);
  const discountAmount = Number(invoice.InvoiceDiscount || 0);

  // Payment status
  const { label: paymentStatus, color: paymentColor } = getPaymentStatus(paidAmount, totalIncl);

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const invoiceDate = lineItems.length > 0 && lineItems[0].CreatedDate 
    ? lineItems[0].CreatedDate 
    : invoice.InvoiceDate || invoice.CreatedDate;

  const handleEdit = () => {
    navigate(`/invoices/edit/${invoice.Id}`);
  };

  const handleDelete = async () => {
    // Block deletion if invoice was sent to Peppol
    if (invoice.IsSendOnPeppol) {
      toast.error(t('invoice_delete_peppol_error') || 'Cannot delete an invoice that has been sent to Peppol.');
      return;
    }

    // Verify this is the last invoice (highest InvoiceNumber)
    try {
      const allInvoices = await invoiceService.getInvoices();
      if (Array.isArray(allInvoices) && allInvoices.length > 0) {
        // Sort by InvoiceNumber descending (handles numeric and alphanumeric)
        const sorted = [...allInvoices].sort((a, b) => {
          const numA = parseInt((a.InvoiceNumber || '').replace(/\D/g, ''), 10) || 0;
          const numB = parseInt((b.InvoiceNumber || '').replace(/\D/g, ''), 10) || 0;
          return numB - numA;
        });
        const lastInvoice = sorted[0];
        if (lastInvoice.Id !== invoice.Id) {
          toast.error(t('invoice_delete_not_last_error') || 'Only the last invoice can be deleted.');
          return;
        }
      }
    } catch {
      toast.error('Failed to verify invoice order. Please try again.');
      return;
    }

    if (window.confirm(t('invoice_delete_confirm') || 'Are you sure you want to delete this invoice?')) {
      try {
        await invoiceService.deleteInvoice(invoice.Id);
        toast.success(t('invoice_delete_success') || 'Invoice deleted successfully.');
        navigate('/invoices');
      } catch (error) {
        toast.error(t('invoice_delete_failed') || 'Failed to delete invoice.');
      }
    }
  };

  const handleSendToPeppol = async () => {
    if (!customer.CustomerPeppolId) {
      toast.error('Customer does not have a Peppol ID');
      return;
    }

    // Generate preview
    setLoadingPreview(true);
    try {
      if (storeInfo) {
        const previewHtml = await renderInvoiceTemplate(invoice, storeInfo, t);
        setPeppolPreviewHtml(previewHtml);
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate invoice preview');
    } finally {
      setLoadingPreview(false);
    }

    // Open dialog for confirmation
    setPeppolSendOnPeppol(true);
    setPeppolAttachPdf(true);
    setOpenPeppolDialog(true);
  };

  const handleClosePeppolDialog = () => {
    setOpenPeppolDialog(false);
    setPeppolSendOnPeppol(false);
    setPeppolAttachPdf(false);
    setPeppolPreviewHtml('');
  };

  const handleConfirmSendToPeppol = async () => {
    if (!peppolSendOnPeppol) {
      toast.error(t('please_confirm_send_to_peppol') || 'Please confirm sending to Peppol');
      return;
    }

    try {
      setSendingToPeppol(true);
      setOpenPeppolDialog(false);
      setLoading(true);
      
      // Create FormData for the request
      const formData = new FormData();
      formData.append('InvoiceId', invoice.Id);
      formData.append('IncludeAttachments', peppolAttachPdf);
      
      // If attach PDF is enabled, generate and include PDF
      if (peppolAttachPdf) {
        if (!storeInfo) {
          toast.error('Store information not loaded. Please refresh the page.');
          return;
        }

        // Generate PDF using the utility service
        const pdfBlob = await generateInvoicePDFComplete(invoice, storeInfo, t);
        formData.append('PdfAttachment', pdfBlob, `${invoice.InvoiceNumber}.pdf`);
      }

      await invoiceService.sendInvoiceToPeppol(formData);
      
      toast.success('Invoice successfully sent to Peppol!');
      // Refresh invoice data
      const invoiceData = await invoiceService.getInvoiceById(invoice.Id);
      setInvoice(invoiceData);
    } catch (error) {
      console.error('Error sending to Peppol:', error);
      toast.error(error.message || 'Failed to send invoice to Peppol');
    } finally {
      setLoading(false);
      setSendingToPeppol(false);
      handleClosePeppolDialog();
    }
  };
  
  // Email sending handler - Blocked from executing API calls
  const handleSendByEmail = async () => {
    // ⛔ FEATURE DISABLED: Prevent any email sending
    toast.info('Email functionality is currently disabled');
    return;
    // The rest of the code is unreachable - protects against browser inspect tampering
  };
  
  const handleCloseEmailDialog = () => {
    setOpenEmailDialog(false);
  };
  
  const handleConfirmSendByEmail = async () => {
    // ⛔ FEATURE DISABLED: Prevent any email sending
    toast.info('Email functionality is currently disabled');
    return;
    // The rest of the code is unreachable - protects against browser inspect tampering
  };
  
  // PDF Preview handler
  const handlePreviewInvoicePdf = async () => {
    setPdfPreviewLoading(true);
    try {
      await generateAndPreviewPDF(invoice, storeInfo, '/invoicetemplates/template-en.html', t);
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      toast.error('Failed to generate PDF preview');
    } finally {
      setPdfPreviewLoading(false);
    }
  };
  
  const handleOpenPaymentDialog = () => {
    const remaining = dueAmount;
    setPaymentAmount(remaining.toFixed(2));
    setIsFullPayment(true);
    // Set first available payment method if none selected
    if (!paymentMethod && paymentMethodOptions.length > 0) {
      setPaymentMethod(paymentMethodOptions[0].value);
    }
    setOpenPaymentDialog(true);
  };
  
  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setPaymentAmount('');
    setPaymentMethod(paymentMethodOptions.length > 0 ? paymentMethodOptions[0].value : '');
    setPaymentNotes('');
    setIsFullPayment(false);
  };
  
  const handlePaymentAmountChange = (e) => {
    const value = e.target.value;
    setPaymentAmount(value);
    const amount = parseFloat(value) || 0;
    setIsFullPayment(amount >= dueAmount);
  };
  
  const handleRecordPayment = async () => {
    const amount = parseFloat(paymentAmount);
    
    if (!amount || amount <= 0) {
      toast.error(t('invalid_payment_amount') || 'Please enter a valid payment amount');
      return;
    }
    
    if (amount > dueAmount) {
      toast.error(t('payment_exceeds_due') || 'Payment amount cannot exceed due amount');
      return;
    }
    
    if (!paymentMethod) {
      toast.error(t('select_payment_method') || 'Please select a payment method');
      return;
    }
    
    // Validate payment methods are loaded
    if (!paymentMethodOptions || paymentMethodOptions.length === 0) {
      toast.error(t('payment_methods_not_loaded') || 'Payment methods not loaded. Please refresh the page.');
      return;
    }
    
    try {
      setProcessingPayment(true);
      
      const selectedMethod = paymentMethodOptions.find(opt => opt.value === paymentMethod);
      
      if (!selectedMethod || !selectedMethod.id) {
        toast.error(t('invalid_payment_method') || 'Invalid payment method selected');
        setProcessingPayment(false);
        return;
      }
      
      // Match backend model exactly
      const paymentData = {
        InvoiceId: invoice.Id,
        PaymentAmount: amount, // Backend expects PaymentAmount (decimal)
        PaymentMethodId: selectedMethod.id, // Backend expects PaymentMethodId (int) - Required
        PaymentMethod: selectedMethod.label, // Backend expects PaymentMethod (string) - Optional
        Notes: paymentNotes || null // Backend expects Notes (string) - Optional
      };
      
      await invoiceService.recordInvoicePayment(paymentData);
      
      toast.success(t('payment_recorded_success') || 'Payment recorded successfully');
      
      // Refresh invoice data
      const invoiceData = await invoiceService.getInvoiceById(invoice.Id);
      setInvoice(invoiceData);
      
      handleClosePaymentDialog();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error(error.message || t('payment_record_failed') || 'Failed to record payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ color: '#667eea' }}>
                Invoice #{invoice.InvoiceNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Created: {formatDate(invoiceDate)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/invoices')}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Back
            </Button>
            {/* PDF Preview Button */}
            <Button
              variant="outlined"
              color="error"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePreviewInvoicePdf}
              disabled={pdfPreviewLoading}
              sx={{ borderRadius: 2 }}
            >
              {pdfPreviewLoading ? 'Generating...' : 'Preview PDF'}
            </Button>
            {/* Show Send/Retry to Peppol based on status */}
            {customer.CustomerPeppolId && (
              invoice.PeppolStatus?.toLowerCase() === 'failed' || invoice.PeppolStatus?.toLowerCase() === 'error' ? (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<RefreshIcon />}
                  onClick={handleSendToPeppol}
                  sx={{ borderRadius: 2 }}
                >
                  Retry to Peppol
                </Button>
              ) : !invoice.IsSendOnPeppol && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleSendToPeppol}
                  sx={{ borderRadius: 2 }}
                >
                  Send to Peppol
                </Button>
              )
            )}
            {/* Send Email Button - Visible but disabled */}
            {customer.Email && !invoice.IsEmailSend && (
              <Button
                variant="contained"
                color="info"
                startIcon={<SendIcon />}
                onClick={handleSendByEmail}
                disabled={true}
                sx={{ borderRadius: 2 }}
                title="Email functionality is currently disabled"
              >
                Send Email
              </Button>
            )}
            {(paymentStatus === PAYMENT_STATUS.NOT_PAID || paymentStatus === PAYMENT_STATUS.PARTIAL_PAID) && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PaymentIcon />}
                onClick={handleOpenPaymentDialog}
                sx={{ borderRadius: 2 }}
              >
                Record Payment
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              disabled={invoice.IsSendOnPeppol}
              sx={{ borderRadius: 2 }}
              title={invoice.IsSendOnPeppol ? 'Cannot edit invoices sent via Peppol' : 'Edit invoice'}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={invoice.IsSendOnPeppol}
              sx={{ borderRadius: 2 }}
              title={invoice.IsSendOnPeppol ? 'Cannot delete invoices sent via Peppol' : 'Delete invoice'}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Peppol Lock Alert */}
      {invoice.IsSendOnPeppol && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>🔒 Peppol Invoice</AlertTitle>
          This invoice has been sent via Peppol and cannot be edited or deleted for compliance reasons.
        </Alert>
      )}

      {/* Payment Status Alert */}
      {paymentStatus === PAYMENT_STATUS.PAID && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>✓ Fully Paid</AlertTitle>
          This invoice has been fully paid.
        </Alert>
      )}

      {paymentStatus === PAYMENT_STATUS.PARTIAL_PAID && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>⚠ Partial Paid</AlertTitle>
          This invoice has been partially paid. Due amount: €{dueAmount.toFixed(2)}
        </Alert>
      )}

      {paymentStatus === PAYMENT_STATUS.NOT_PAID && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>✗ Not Paid</AlertTitle>
          This invoice has not been paid yet. Total due: €{dueAmount.toFixed(2)}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Company Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon sx={{ color: '#667eea', mr: 1 }} />
              <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                Company Information
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight={500}>{storeInfo?.name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">VAT Number</Typography>
                <Typography variant="body1">{storeInfo?.vat || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{storeInfo?.email || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{storeInfo?.phone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                <Typography variant="body1">
                  {storeInfo?.address || 'N/A'}
                  {storeInfo?.postCode && `, ${storeInfo.postCode}`}
                  {storeInfo?.city && ` ${storeInfo.city}`}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">IBAN</Typography>
                <Typography variant="body1">{storeInfo?.iban || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Customer Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ color: '#667eea', mr: 1 }} />
              <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                Customer Information
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight={500}>{customerName}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">VAT Number</Typography>
                <Typography variant="body1">{customerVAT}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{customerEmail}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{customerPhone}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                <Typography variant="body1">
                  {customerAddress}
                  {customerPostCode && `, ${customerPostCode}`}
                  {customerCity && ` ${customerCity}`}
                  {customerCountry && `, ${customerCountry}`}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Invoice Summary */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              Invoice Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Invoice Number</Typography>
                <Typography variant="body1" fontWeight={500}>{invoice.InvoiceNumber}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Payment Status</Typography>
                <Chip
                  label={paymentStatus}
                  color={paymentColor}
                  sx={{ mt: 1, fontWeight: 600, fontSize: '0.875rem' }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Invoice Date</Typography>
                <Typography variant="body1">{formatDate(invoiceDate)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Due Date</Typography>
                <Typography variant="body1">{formatDate(invoice.PaymentDueDate || invoice.DueDate)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Subtotal (Excl. VAT)</Typography>
                <Typography variant="h6" fontWeight={600}>€{totalExcl.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">VAT ({vatPercentage}%)</Typography>
                <Typography variant="h6" fontWeight={600}>€{totalVAT.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Total (Incl. VAT)</Typography>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#667eea' }}>
                  €{totalIncl.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Paid Amount</Typography>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#4caf50' }}>
                  €{paidAmount.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Due Amount</Typography>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#f44336' }}>
                  €{dueAmount.toFixed(2)}
                </Typography>
              </Grid>
              
              {/* Payment Methods Section */}
              {aggregatedPayments.length > 0 ? (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Payment Methods
                    </Typography>
                    {aggregatedPayments.map((payment, index) => (
                      <Box 
                        key={index}
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          py: 0.5,
                          borderBottom: index < aggregatedPayments.length - 1 ? '1px solid #f0f0f0' : 'none'
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {payment.methodName}:
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          €{payment.totalAmount.toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Grid>
                </>
              ) : invoice.PaymentMethod && paidAmount > 0 && !invoice.PaymentMethod.includes('/') ? (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Payment Method</Typography>
                    <Chip 
                      label={invoice.PaymentMethod} 
                      size="small" 
                      color="primary"
                      sx={{ mt: 1, fontWeight: 600 }}
                    />
                  </Grid>
                </>
              ) : null}
            </Grid>
          </Paper>
        </Grid>

        {/* Line Items */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              Line Items
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>SKU/ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">VAT %</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Total (Excl.)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.length > 0 ? (
                    lineItems.map((item, index) => {
                      const itemTotalExcl = Number(item.TotalExcl || 0);
                      // ✅ Use stored TotalIncl if available, otherwise calculate
                      const itemTotalIncl = item.TotalIncl ? Number(item.TotalIncl) : itemTotalExcl * (1 + vatPercentage / 100);
                      const itemVatAmount = itemTotalIncl - itemTotalExcl;
                      
                      return (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography fontWeight={500}>{item.DisplayName || item.ProductName || item.Name || 'N/A'}</Typography>
                          {item.Description && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {item.Description}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.Reference || item.ProductId ? (
                            <Chip 
                              label={item.Reference || item.ProductId} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ) : 'N/A'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={item.Quantity || 0} 
                            size="small" 
                            color="info"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={500}>€{Number(item.UnitPrice || item.Price || 0).toFixed(2)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={`${vatPercentage}%`} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#667eea' }}>
                          €{itemTotalExcl.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    )})
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: '#888', py: 4 }}>
                        No line items found
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* Totals Row */}
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      Subtotal (Excl. VAT):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                      €{totalExcl.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      VAT ({vatPercentage}%):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                      €{totalVAT.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.15)' }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      Total (Incl. VAT):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#667eea' }}>
                      €{totalIncl.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Payment History Section */}
        {aggregatedPayments.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PaymentIcon sx={{ color: '#667eea', mr: 1 }} />
                <Typography variant="h6" fontWeight="700" sx={{ color: '#667eea' }}>
                  Payment History
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Payment Method</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Amount Paid</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Payment Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.Payments && invoice.Payments.map((payment, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip 
                            label={payment.PaymentMethodName || payment.PaymentMethod?.Name || 'Unknown Method'}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" fontWeight={600} sx={{ color: '#4caf50' }}>
                            €{Number(payment.Amount || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {payment.PaymentDate ? formatDate(payment.PaymentDate) : formatDate(payment.CreatedDate || invoice.CreatedDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {payment.Notes || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.15)' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>
                        Total Paid:
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#4caf50' }}>
                        €{paidAmount.toFixed(2)}
                      </TableCell>
                      <TableCell colSpan={2}>
                        {dueAmount > 0 ? (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Remaining Due:
                            </Typography>
                            <Typography variant="h6" fontWeight={700} sx={{ color: '#f44336' }}>
                              €{dueAmount.toFixed(2)}
                            </Typography>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              startIcon={<PaymentIcon />}
                              onClick={handleOpenPaymentDialog}
                              sx={{ 
                                borderRadius: 2,
                                ml: 2,
                                fontWeight: 600
                              }}
                            >
                              Record Payment
                            </Button>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                            <Chip
                              label="Fully Paid"
                              color="success"
                              icon={<PaymentIcon />}
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        {/* Peppol Information if sent */}
        {invoice.IsSendOnPeppol && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
                Peppol Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={invoice.PeppolStatus || 'Not Sent'} 
                    color={invoice.PeppolStatus === 'Sent' ? 'success' : 'default'}
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Message ID</Typography>
                  <Typography variant="body2">{invoice.PeppolMessageId || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">Sent Date</Typography>
                  <Typography variant="body2">{invoice.PeppolSentDate ? formatDate(invoice.PeppolSentDate) : 'N/A'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Custom Notes if available */}
        {invoice.CustomText && invoice.CustomText.trim() !== '' && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
                Additional Notes
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {invoice.CustomText}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
      
      {/* Payment Dialog */}
      <Dialog 
        open={openPaymentDialog} 
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#667eea', color: 'white', fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PaymentIcon sx={{ mr: 1 }} />
            Record Payment
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                <Typography variant="h6" fontWeight={600}>€{totalIncl.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Amount Due</Typography>
                <Typography variant="h6" fontWeight={600} color="error">€{dueAmount.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Already Paid</Typography>
                <Typography variant="h6" fontWeight={600} color="success.main">€{paidAmount.toFixed(2)}</Typography>
              </Grid>
            </Grid>
          </Box>
          
          <TextField
            fullWidth
            label="Payment Amount"
            type="number"
            value={paymentAmount}
            onChange={handlePaymentAmountChange}
            inputProps={{ min: 0, step: 0.01, max: dueAmount }}
            sx={{ mb: 2 }}
            helperText={isFullPayment ? 'Full payment' : 'Partial payment'}
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Payment Method *</InputLabel>
            <Select
              value={paymentMethod}
              label="Payment Method *"
              onChange={(e) => setPaymentMethod(e.target.value)}
              required
              disabled={paymentMethodOptions.length === 0}
            >
              {paymentMethodOptions.length > 0 ? (
                paymentMethodOptions.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No payment methods available
                </MenuItem>
              )}
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={3}
            value={paymentNotes}
            onChange={(e) => setPaymentNotes(e.target.value)}
            inputProps={{ maxLength: 500 }}
            sx={{ mb: 2 }}
            helperText={`${paymentNotes.length}/500 characters`}
          />
          
          <Alert severity="info" sx={{ mt: 2 }}>
            {isFullPayment 
              ? 'This will mark the invoice as fully paid.' 
              : 'This will record a partial payment and the invoice will remain partially paid.'}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleClosePaymentDialog}
            disabled={processingPayment}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRecordPayment}
            variant="contained"
            color="primary"
            disabled={processingPayment || !paymentAmount || parseFloat(paymentAmount) <= 0 || !paymentMethod || paymentMethodOptions.length === 0}
            sx={{ minWidth: 120 }}
          >
            {processingPayment ? 'Processing...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Peppol Send/Retry Dialog */}
      <Dialog 
        open={openPeppolDialog} 
        onClose={handleClosePeppolDialog}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#667eea', color: 'white', fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SendIcon sx={{ mr: 1 }} />
            {invoice.PeppolStatus?.toLowerCase() === 'failed' || invoice.PeppolStatus?.toLowerCase() === 'error' 
              ? 'Retry Sending to Peppol' 
              : 'Send to Peppol'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {/* Invoice Preview */}
          {loadingPreview ? (
            <Box 
              sx={{ 
                mb: 3, 
                p: 6, 
                textAlign: 'center', 
                bgcolor: '#f5f5f5', 
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >
              <CircularProgress size={50} />
              <Typography variant="body1" color="text.secondary">
                Preparing invoice preview...
              </Typography>
            </Box>
          ) : peppolPreviewHtml ? (
            <Box 
              sx={{ 
                mb: 3, 
                maxHeight: '700px', 
                overflowY: 'auto', 
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                bgcolor: 'white'
              }}
            >
              <div 
                dangerouslySetInnerHTML={{ __html: peppolPreviewHtml }}
                style={{ 
                  transform: 'scale(0.95)', 
                  transformOrigin: 'top left',
                  width: '105.26%',
                  padding: '20px'
                }}
              />
            </Box>
          ) : (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Invoice Details
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                #{invoice.InvoiceNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Customer: {customerName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Amount: €{totalIncl.toFixed(2)}
              </Typography>
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          <FormControlLabel
            control={
              <Switch
                checked={peppolSendOnPeppol}
                onChange={(e) => setPeppolSendOnPeppol(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Send to Peppol Network *
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Confirm you want to send this invoice via Peppol
                </Typography>
              </Box>
            }
            sx={{ mb: 2, alignItems: 'flex-start' }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={peppolAttachPdf}
                onChange={(e) => setPeppolAttachPdf(e.target.checked)}
                color="primary"
                disabled={!peppolSendOnPeppol}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Attach PDF to Invoice
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Include a PDF version of the invoice
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start' }}
          />

          {!peppolSendOnPeppol && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please confirm sending to Peppol before proceeding
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClosePeppolDialog} disabled={sendingToPeppol}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmSendToPeppol}
            variant="contained"
            disabled={sendingToPeppol || !peppolSendOnPeppol}
            sx={{ 
              minWidth: 120,
              bgcolor: '#667eea',
              '&:hover': { bgcolor: '#5568d3' }
            }}
          >
            {sendingToPeppol ? 'Sending...' : 'Confirm & Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Send Dialog - Visible but functionality disabled */}
      <Dialog 
        open={openEmailDialog} 
        onClose={handleCloseEmailDialog}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#2196f3', color: 'white', fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SendIcon sx={{ mr: 1 }} />
            Send Invoice by Email
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {loadingEmailPreview ? (
            <Box 
              sx={{ 
                p: 6, 
                textAlign: 'center', 
                bgcolor: '#f5f5f5', 
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}
            >
              <CircularProgress size={50} />
              <Typography variant="body1" color="text.secondary">
                Preparing invoice preview...
              </Typography>
            </Box>
          ) : emailPreviewHtml ? (
            <Box 
              sx={{ 
                maxHeight: '500px', 
                overflowY: 'auto', 
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                bgcolor: 'white'
              }}
            >
              <div 
                dangerouslySetInnerHTML={{ __html: emailPreviewHtml }}
                style={{ 
                  transform: 'scale(0.95)', 
                  transformOrigin: 'top left',
                  width: '105.26%',
                  padding: '20px'
                }}
              />
            </Box>
          ) : (
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Email Feature Disabled</AlertTitle>
                This functionality is currently disabled and cannot be used.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Invoice Details
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                #{invoice.InvoiceNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Customer: {customerName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Amount: €{totalIncl.toFixed(2)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #90caf9', flex: 1, mr: 2 }}>
            <SendIcon color="info" fontSize="small" />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Send to:
              </Typography>
              <Typography variant="body2" fontWeight={600} color="info.main">
                {customer.Email}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCloseEmailDialog} disabled={sendingEmail}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSendByEmail}
              variant="contained"
              disabled={true}
              title="Email functionality is currently disabled"
              sx={{ 
                minWidth: 120,
                bgcolor: '#2196f3',
                '&:hover': { bgcolor: '#1976d2' }
              }}
            >
              Confirm & Send
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InvoiceViewPage;